// Design-time thread illustration, not the digitizer's final stitch plan.
// A small repeating tile keeps dense designs interactive at any zoom.
export function RealisticFill({ id, clipId, settings, mmToVb, color, viewBox, maskId, bounds }) {
  const spacing = Math.max(0.05, settings.row_spacing_mm || 0.2) * mmToVb;
  const length = Math.max(0.1, settings.max_stitch_length_mm || 3) * mmToVb;
  const stagger = Math.max(1, Math.min(32, Math.round(settings.staggers || 1)));
  const rows = settings.randomize_stitch_length ? stagger * 3 : stagger;
  const width = length * 3;
  const height = spacing * rows;
  // Thread diameter is independent of row spacing: sparse fills stay sparse.
  const diameter = 0.23 * mmToVb;
  const bodyId = `${id}-thread-body`;
  const endsId = `${id}-thread-ends`;
  const patternId = `${id}-thread-pattern`;
  const stitches = [];
  for (let row = -1; row <= rows; row++) {
    const wrapped = (row + rows) % rows;
    const phase = (wrapped % stagger) / stagger;
    const jitterAt = (column) => settings.randomize_stitch_length
      ? Math.sin(wrapped * 12.9898 + ((column % 3 + 3) % 3) * 78.233)
        * Math.min(0.3, (settings.random_stitch_length_jitter_percent ?? 25) / 200)
      : 0;
    for (let column = -2; column < 4; column++) {
      const x = (column + phase + jitterAt(column)) * length;
      const segmentLength = (1 + jitterAt(column + 1) - jitterAt(column)) * length;
      const y = row * spacing;
      stitches.push(
        <g key={`${row}-${column}`}>
          <rect x={x} y={y - diameter * 0.38} width={segmentLength} height={diameter}
            rx={diameter * 0.45} fill="#160f19" opacity="0.24" />
          <rect x={x} y={y - diameter / 2} width={segmentLength} height={diameter}
            rx={diameter * 0.45} fill={color} />
          <rect x={x} y={y - diameter / 2} width={segmentLength} height={diameter}
            rx={diameter * 0.45} fill={`url(#${bodyId})`} />
          <rect x={x} y={y - diameter / 2} width={segmentLength} height={diameter}
            rx={diameter * 0.45} fill={`url(#${endsId})`} />
          <path d={`M${x + Math.min(diameter * 0.4, segmentLength / 3)},${y - diameter * 0.12}L${x + segmentLength - Math.min(diameter * 0.4, segmentLength / 3)},${y - diameter * 0.12}`}
            stroke="white" strokeWidth={diameter * 0.045} opacity="0.28" />
        </g>
      );
    }
  }
  return (
    <g mask={maskId ? `url(#${maskId})` : undefined} clipPath={maskId ? undefined : `url(#${clipId})`}>
      <defs>
        <linearGradient id={bodyId} x1="0" y1="0" x2="0" y2="1" >
          <stop offset="0" stopColor={color} />
          <stop offset="0.12" stopColor="#000" stopOpacity="0.38" />
          <stop offset="0.38" stopColor="#fff" stopOpacity="0.62" />
          <stop offset="0.56" stopColor={color} />
          <stop offset="1" stopColor="#000" stopOpacity="0.48" />
        </linearGradient>
        <linearGradient id={endsId}>
          <stop offset="0" stopColor="#100a14" stopOpacity="0.65" />
          <stop offset="0.035" stopColor="#100a14" stopOpacity="0" />
          <stop offset="0.95" stopColor="#100a14" stopOpacity="0" />
          <stop offset="1" stopColor="#100a14" stopOpacity="0.55" />
        </linearGradient>
        <pattern id={patternId} width={width} height={height} patternUnits="userSpaceOnUse"
          patternTransform={`rotate(${settings.angle ?? 45})`}>
          {stitches}
        </pattern>
      </defs>
      <rect {...(bounds || { x: viewBox.vbX, y: viewBox.vbY, width: viewBox.vbW, height: viewBox.vbH })}
        fill={`url(#${patternId})`} />
    </g>
  );
}
